/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Button from '@Button';
import Text from '@Text';
import FetchStatus from '../../Data/FetchStatus';
import {
  getClientApiSolarAction,
  getClientApiVerifyPotentialAction,
} from '../../Hooks/actionCreators';
import {
  goToNextScreen,
  goToPreviousScreen,
  resetSolar,
  setSolarLocationOverride,
  setStepState,
} from '../../Lib/redux/slices/clientApiJourney';
import { selectUpstreamLocationStepKey } from '../../Helpers/clientApiJourneyPosition.helpers';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import { useFallThroughToNormalLead } from '../../Hooks/useFallThroughToNormalLead';
import { FALL_THROUGH_REASONS } from './clientApiJourney.constants';
import useClientApiCopy from './copy/useClientApiCopy';
import { computeTotals } from './solarPotential.helpers';
import SolarMap from './SolarMap';
import SolarMapPinModal from './SolarMapPinModal';
import SolarPotentialCard from './SolarPotentialCard';
import RoofChangeQuestion from './RoofChangeQuestion';
import FailureNotice from './FailureNotice';
import makeSolarPotentialStyles from './makeSolarPotentialStyles';

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 7V11M8 5V5.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const formatSavings = (amount, currencyCode, localeCode) => {
  const locale = (localeCode ?? 'en-GB').replace('_', '-');
  if (!currencyCode) return amount.toLocaleString(locale);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(amount);
};

const solarFallThroughReason = ({ loadStatus, verifyStatus }) => {
  if (loadStatus === FetchStatus.FAILED)
    return FALL_THROUGH_REASONS.SOLAR_LOAD_FAILED;
  if (verifyStatus === FetchStatus.FAILED)
    return FALL_THROUGH_REASONS.SOLAR_VERIFY_FAILED;
  return FALL_THROUGH_REASONS.NO_SOLAR_POTENTIAL;
};

const SolarPotential = ({ stepKey }) => {
  const theme = useTheme();
  const classes = makeSolarPotentialStyles({ theme });
  const dispatch = useDispatch();
  const copy = useClientApiCopy();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  // Only advance for a verify started on this visit. The status is kept in the
  // store, so on a return visit (Back from the next screen) it is still
  // COMPLETED and would otherwise bounce the user straight forward again.
  const hasSubmitted = useRef(false);

  const loadStatus = useSelector(
    (state) => state.clientApiJourney.solar?.loadStatus
  );
  const verifyStatus = useSelector(
    (state) => state.clientApiJourney.solar?.verifyStatus
  );
  const data = useSelector(
    (state) => state.clientApiJourney.stepResponses?.[stepKey]?.load?.data
  );
  const roofChanges = useSelector(
    (state) => state.clientApiJourney.stepStates?.[stepKey]?.roofChanges
  );
  const localeCode = useSelector((state) => state.formConfig?.localeCode);
  const locationStepKey = useSelector(selectUpstreamLocationStepKey);
  const selectedLocation = useSelector((state) =>
    locationStepKey
      ? state.clientApiJourney.stepStates?.[locationStepKey]?.location
      : undefined
  );
  const locationOverride = useSelector(
    (state) => state.clientApiJourney.solar?.locationOverride
  );

  useWidgetHeightRefresh([loadStatus, isPinModalOpen]);

  useEffect(() => {
    if (loadStatus !== FetchStatus.NOT_STARTED) return;
    dispatch(getClientApiSolarAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = data ? computeTotals(data.sides, data) : undefined;
  // No roof faces, or none a panel fits on, leaves nothing to assess, so the
  // journey fails here like a failed lookup rather than showing zero savings.
  const hasNoSolarPotential = totals?.maxPanels === 0;
  const hasFailed =
    loadStatus === FetchStatus.FAILED ||
    verifyStatus === FetchStatus.FAILED ||
    hasNoSolarPotential;

  useFallThroughToNormalLead({
    shouldFallThrough: hasFailed,
    reason: solarFallThroughReason({ loadStatus, verifyStatus }),
  });

  useEffect(() => {
    if (!hasSubmitted.current || verifyStatus !== FetchStatus.COMPLETED) return;
    dispatch(goToNextScreen());
  }, [verifyStatus, dispatch]);

  if (hasFailed) {
    return (
      <FailureNotice
        heading={copy.solar.loadFailed.heading}
        subheading={copy.solar.loadFailed.subheading}
        dataTestId="solarLoadFailed"
      />
    );
  }

  if (loadStatus !== FetchStatus.COMPLETED || !data) {
    return (
      <Box css={classes.loading} data-testid="solarLoading">
        <Text variant="text">{copy.solar.loading}</Text>
      </Box>
    );
  }

  // The answer lives in the step state (surviving Back/remount). Writing both
  // fields together is required because setStepState replaces the step's state.
  const handleRoofChange = (value) => {
    dispatch(
      setStepState({
        stepKey,
        state: { roofChanges: value, selectedSides: data.sides ?? [] },
      })
    );
  };

  const handleContinue = () => {
    if (verifyStatus === FetchStatus.IN_PROGRESS) return;
    hasSubmitted.current = true;
    dispatch(getClientApiVerifyPotentialAction());
  };

  const handleBack = () => {
    dispatch(goToPreviousScreen());
  };

  // resetSolar must run before the override is set: it rebuilds the solar state
  // from scratch, so setting the override first would wipe it. It also clears the
  // roof answer and sides, which verify re-sends from Own.state and so must come
  // from the new building's response, not the old one.
  const handlePinConfirm = (coords) => {
    dispatch(resetSolar());
    dispatch(setSolarLocationOverride({ locationOverride: coords }));
    dispatch(getClientApiSolarAction());
    setIsPinModalOpen(false);
  };

  const canReselect =
    Boolean(selectedLocation) &&
    typeof selectedLocation.latitude === 'number' &&
    typeof selectedLocation.longitude === 'number' &&
    verifyStatus !== FetchStatus.IN_PROGRESS;

  // Start the pin where the customer last placed it, so reopening after a confirm
  // returns to the assessed building rather than the original address.
  const pinStart = locationOverride ?? {
    latitude: selectedLocation?.latitude,
    longitude: selectedLocation?.longitude,
  };

  return (
    <Box css={classes.container}>
      <Box css={classes.backButton}>
        <MuiButton
          css={classes.backLink}
          onClick={handleBack}
          data-testid="solarBack"
        >
          {copy.solar.back}
        </MuiButton>
      </Box>
      <Box css={classes.title}>
        <Text variant="question" align="left">
          {copy.solar.title}
        </Text>
      </Box>
      <Box css={classes.subheading}>{copy.solar.subheading}</Box>
      <Box css={classes.banner} data-testid="solarSavingsBanner">
        <Box css={classes.bannerLabel}>
          <span>{copy.solar.savingsLabel}</span>
          <button
            type="button"
            css={classes.infoButton}
            data-testid="solarSavingsInfo"
            aria-label={copy.solar.savingsLabel}
            onClick={() => setIsTooltipOpen((open) => !open)}
            onMouseEnter={() => setIsTooltipOpen(true)}
            onMouseLeave={() => setIsTooltipOpen(false)}
          >
            <InfoIcon />
            {isTooltipOpen && (
              <Box css={classes.tooltip} data-testid="solarSavingsTooltip">
                {copy.solar.savingsTooltip}
              </Box>
            )}
          </button>
        </Box>
        <Box css={classes.bannerAmount}>
          {formatSavings(totals.annualSavings, totals.currencyCode, localeCode)}
        </Box>
      </Box>
      <SolarMap sides={data.sides} localeCode={localeCode} />
      {canReselect && (
        <Box css={classes.pinLink}>
          <MuiButton
            css={classes.pinLinkButton}
            onClick={() => setIsPinModalOpen(true)}
            data-testid="solarPinLink"
          >
            {copy.solar.pinLink}
          </MuiButton>
        </Box>
      )}
      {canReselect && (
        <SolarMapPinModal
          open={isPinModalOpen}
          start={pinStart}
          address={selectedLocation.address}
          localeCode={localeCode}
          onConfirm={handlePinConfirm}
          onClose={() => setIsPinModalOpen(false)}
        />
      )}
      <RoofChangeQuestion
        solarPotentialAge={data.solarPotentialAge}
        localeCode={localeCode}
        value={roofChanges}
        onChange={handleRoofChange}
      />
      <SolarPotentialCard
        totals={totals}
        copy={copy}
        classes={classes}
        localeCode={localeCode}
      />
      <Box css={classes.continueButton}>
        <Button
          dataTestId="solarContinue"
          label={copy.solar.continue}
          onClick={handleContinue}
          disabled={
            roofChanges === undefined ||
            verifyStatus === FetchStatus.IN_PROGRESS
          }
        />
      </Box>
    </Box>
  );
};

export default SolarPotential;
